import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { ChevronLeft, Clock, Settings, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bookingCutoff, setBookingCutoff] = useState(30);
  const [originalCutoff, setOriginalCutoff] = useState(30);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/settings');
        const minutes = res.data?.booking_cutoff_minutes ?? 30;
        setBookingCutoff(minutes);
        setOriginalCutoff(minutes);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dirty = bookingCutoff !== originalCutoff;

  const handleSave = async () => {
    const v = parseInt(bookingCutoff, 10);
    if (Number.isNaN(v) || v < 0 || v > 1440) {
      toast.error('Cutoff must be between 0 and 1440 minutes');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/settings', { booking_cutoff_minutes: v });
      setOriginalCutoff(v);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const quickValues = [15, 30, 45, 60, 90, 120];

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-8" data-testid="admin-settings-page">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-zinc-400 hover:text-white"
          onClick={() => navigate('/admin')}
          data-testid="back-to-dashboard-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
            <Settings className="w-7 h-7 text-purple-300" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Shop Settings</h1>
            <p className="text-zinc-400 text-sm mt-1">Global rules that affect every customer.</p>
          </div>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-lime-400" /> Same-day booking cutoff
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-400 mb-4">
              Block customers from booking a time slot that's less than{' '}
              <span className="text-lime-400 font-semibold">{bookingCutoff} minutes</span> from now.
              Helps avoid surprise walk-ins that crash a barber mid-cut.
            </p>

            {loading ? (
              <div className="py-6 flex items-center justify-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <>
                <Label htmlFor="cutoff" className="text-zinc-300">Cutoff (minutes)</Label>
                <div className="relative mt-1">
                  <Input
                    id="cutoff"
                    type="number"
                    min="0"
                    max="1440"
                    value={bookingCutoff}
                    onChange={(e) => setBookingCutoff(parseInt(e.target.value || '0', 10))}
                    className="bg-zinc-950 border-zinc-700 text-white pr-16"
                    data-testid="cutoff-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">
                    min
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {quickValues.map((v) => (
                    <button
                      key={v}
                      onClick={() => setBookingCutoff(v)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        bookingCutoff === v
                          ? 'bg-lime-400 text-black'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                      data-testid={`cutoff-preset-${v}`}
                    >
                      {v} min
                    </button>
                  ))}
                </div>

                <div className="mt-5 p-3 rounded-lg bg-zinc-950/80 border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Live preview</p>
                  <p className="text-sm text-zinc-300">
                    {bookingCutoff === 0
                      ? "Customers can book any open slot — even ones starting right now."
                      : `If the current time is 4:00 PM and cutoff is ${bookingCutoff} min, customers can only book slots starting at ${(() => {
                          const t = new Date();
                          t.setHours(16, 0 + bookingCutoff, 0, 0);
                          return t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                        })()} or later.`}
                  </p>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    className="bg-lime-400 text-black hover:bg-lime-500 font-semibold"
                    data-testid="save-settings-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
